/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useAsyncEffect } from 'src/core/hooks/useAsyncEffect';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { PrebuiltDemosSection } from 'src/languageStudio/appShell/landingPage/components/PrebuiltDemosSection';
import { DemoHeader } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoHeader';
import { DemoPageAgreement } from 'src/languageStudio/appShell/prebuiltDemos/components/DemoPageAgreement';
import { DocumentTranslationDemoPageInputStep } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/DocumentTranslationDemoPageInputStep';
import { DocumentTranslatorNextSteps } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/DocumentTranslatorNextSteps';
import { DocumentTranslatorResultStep } from 'src/languageStudio/appShell/prebuiltDemos/translatorDemos/DocumentTranslatorResultStep';
import { headerClassName } from 'src/languageStudio/appShell/prebuiltDemos/utils/commonStyles';
import {
    documentTranslationDocLink,
    documentTranslationSampleLink,
    documentTranslationSDKLink,
    translationPricingLink
} from 'src/languageStudio/appShell/prebuiltDemos/utils/demosSupportLinks';
import { TargetFile, useTranslatorCapabilities } from 'src/languageStudio/core/hooks/useTranslatorCapabilities';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';

const messages = defineMessages({
    pageHeader: { id: 'languageStudio.documentTranslationDemoPage.pageHeader', defaultMessage: 'Translate documents' },
    relatedServicesTitle: { id: 'languageStudio.documentTranslationDemoPage.relatedServicesTitle', defaultMessage: 'Related services' },
    relatedServicesDescription: {
        id: 'languageStudio.documentTranslationDemoPage.relatedServicesDescription',
        defaultMessage: 'Try these customization or other prebuilt capabilities'
    },
    capabilityDescription: {
        id: 'languageStudio.documentTranslationDemoPage.capabilityDescription',
        defaultMessage: `Translate large document files with rich content — translate Word, PowerPoint, Excel, or PDFs,
        and more into`
    },
    supportedLanguagesLink: {
        id: 'languageStudio.documentTranslationDemoPage.supportedLanguagesLink',
        defaultMessage: '90 languages and dialects'
    },
    capabilityDescriptionCont: {
        id: 'languageStudio.documentTranslationDemoPage.capabilityDescriptionCont',
        defaultMessage: `You can also apply custom glossaries and custom models built with Custom Translator to make
        sure your documents translate the way you want.`
    }
});

/**
 * Displays the document translation demo page.
 */
export const DocumentTranslationDemoPage = observer(() => {
    const { localize } = useLocalization();
    const { dispatcher, rootStore } = useGlobalState();
    const storageAccountTracker = useProgressTracker();
    const azureStorageAccountsStoreHouse = useLocalStore<ResourceStoreHouse<AzureStorageAccountStore>>(createResourceStoreHouse);
    const { subscriptionId, csAccountStore } = rootStore.azureStore.session;
    const {
        tracker,
        resetResults,
        supportedLanguages,
        translateDocuments,
        getSupportedLanguages,
        documentsTranslationResult,
        documentsTranslationStatus
    } = useTranslatorCapabilities();
    useAsyncEffect(async () => {
        if (csAccountStore.kind === 'TextTranslation') {
            getSupportedLanguages();
        }
    }, [csAccountStore?.kind]);

    const [sourceLanguage, setSourceLanguage] = React.useState<string>(null);
    const [targetLanguages, setTargetLanguages] = React.useState<string[]>([]);
    const [selectedFiles, setSelectedFiles] = React.useState<TargetFile[]>([]);
    const [selectedStorageAccount, setSelectedStorageAccount] = React.useState('');
    const [selectedDestination, setSelectedDestination] = React.useState<string>(null);
    const [downloadTranslatedFiles, setDownloadTranslatedFiles] = React.useState(false);
    const [selectedGlossaries, setSelectedGlossaries] = React.useState<TargetFile[]>([]);
    const [shouldTranslateContainer, setShouldTranslateContainer] = React.useState(false);

    const accountTokens = rootStore.azureStore.session.userTokens;
    const storageAccount = azureStorageAccountsStoreHouse.items.find(s => s.name === selectedStorageAccount);
    const description = (
        <LuisText>
            {localize(messages.capabilityDescription)}
            &nbsp;
            <ExternalLink href="https://aka.ms/Tarnslate-languages">{localize(messages.supportedLanguagesLink)}</ExternalLink>
            &nbsp;
            {localize(messages.capabilityDescriptionCont)}
        </LuisText>
    );
    const relatedServices = (
        <Stack tokens={{ childrenGap: 15 }}>
            <LuisText>{localize(messages.relatedServicesDescription)}</LuisText>
            <PrebuiltDemosSection demosToDisplay={['textTranslation']} />
        </Stack>
    );

    const fetchStorageAccounts = () => {
        if (csAccountStore.kind === 'TextTranslation') {
            dispatcher.dispatch('getAzureStorageAccountsBySubscriptionId', {
                toastData: {},
                id: subscriptionId,
                tracker: storageAccountTracker,
                localStore: azureStorageAccountsStoreHouse
            });
        }
    };

    React.useEffect(() => {
        if (subscriptionId && accountTokens.storageToken) {
            fetchStorageAccounts();
        } else {
            storageAccountTracker.reset();
        }
    }, [subscriptionId, accountTokens.storageToken]);

    const onTargetLanguageChange = (newLanguageCodes: string[]) => {
        resetResults();
        setTargetLanguages(newLanguageCodes);
    };

    const onSourceFilesChange = (files: TargetFile[], isAllFiles: boolean) => {
        resetResults();
        setSelectedFiles(files);
        setShouldTranslateContainer(isAllFiles);
    };

    const handleDestinationChange = (containerName: string, downloadFiles: boolean) => {
        setSelectedDestination(containerName);
        setDownloadTranslatedFiles(downloadFiles);
    };

    return (
        <Stack tokens={{ childrenGap: 20 }}>
            <DemoHeader
                demoVersion="1.0"
                demoDescription={description}
                supportedPlatforms={['cloud']}
                featureName="document translation"
                getSdkLink={documentTranslationSDKLink}
                demoTitle={localize(messages.pageHeader)}
                learnMoreLink={documentTranslationDocLink}
                documentationLink={documentTranslationDocLink}
                gitHubSamplesLink={documentTranslationSampleLink}
            />

            <DocumentTranslationDemoPageInputStep
                selectedFiles={selectedFiles}
                sourceLanguageKey={sourceLanguage}
                onFilesChange={onSourceFilesChange}
                targetLanguageKeys={targetLanguages}
                supportedLanguages={supportedLanguages}
                onSourceLanguageChange={setSourceLanguage}
                onGlossariesChange={setSelectedGlossaries}
                onDestinationChange={handleDestinationChange}
                refreshStorageAccounts={fetchStorageAccounts}
                selectedStorageAccount={selectedStorageAccount}
                onTargetLanguageChange={onTargetLanguageChange}
                downloadTranslatedFiles={downloadTranslatedFiles}
                onStorageAccountChange={setSelectedStorageAccount}
                storageAccountsError={storageAccountTracker.error}
                selectedDestinationContainer={selectedDestination}
                storageAccounts={azureStorageAccountsStoreHouse.items}
                isStorageAccountsLoading={storageAccountTracker.isRunning}
            />

            <DemoPageAgreement
                featureName="document translation"
                pricingLink={translationPricingLink}
                isRunDisabled={!selectedFiles.length}
                acceptedResourceType="TextTranslation"
                onRunDemoClick={() =>
                    translateDocuments({
                        targetLanguages,
                        files: selectedFiles,
                        downloadTranslatedFiles,
                        glossaryFiles: selectedGlossaries,
                        isAllFiles: shouldTranslateContainer,
                        storageAccountName: selectedStorageAccount,
                        destinationContainerName: selectedDestination,
                        resourceGroupName: storageAccount?.resourceGroupName
                    })
                }
            />

            <DocumentTranslatorResultStep
                translatorTracker={tracker}
                documentsTranslationResult={documentsTranslationResult}
                documentsTranslationStatus={documentsTranslationStatus}
            />

            <DocumentTranslatorNextSteps />

            <EditableTitle
                className={headerClassName}
                title={localize(messages.relatedServicesTitle)}
                titleTextProps={{ variant: 'xLarge', fontWeight: 600 }}
            />

            {relatedServices}
        </Stack>
    );
});
